import { defaultCountries, parseCountry } from 'react-international-phone';

// The checkout stores phone numbers as a DTO separating the country dial
// code from the national number: { code: '55', number: '11987654321' },
// plus the ISO country when the input knows it ({ country: 'br' }).
// The payment endpoints consume it in different shapes (Asaas takes the
// { code, number } pair, the Stripe intent takes the platform's canonical
// Phone object), so the converters live here.

export function buildPhoneDTO(dialCode, fullPhone, country = '') {
  const code = String(dialCode || '').replace(/\D/g, '');
  const digits = String(fullPhone || '').replace(/\D/g, '');
  const number = code && digits.startsWith(code) ? digits.slice(code.length) : digits;

  if (!code || !number) {
    return null;
  }

  const dto: any = { code, number };
  const iso2 = String(country || '').trim().toLowerCase();

  if (iso2.length === 2) {
    dto.country = iso2;
  }

  return dto;
}

export function isPhoneDTO(value) {
  return !!value && typeof value === 'object' && 'code' in value && 'number' in value;
}

// Legacy values (URL prefill, old sessions) are plain strings produced by
// the +55-only input — treat any bare string as a Brazilian number.
export function resolvePhoneDTO(value) {
  if (isPhoneDTO(value)) {
    return value.code && value.number ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const digits = value.replace(/\D/g, '');
    const number = digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits;

    return number ? { code: '55', number, country: 'br' } : null;
  }

  return null;
}

export function phoneToE164(value) {
  const dto = resolvePhoneDTO(value);

  return dto ? `+${dto.code}${dto.number}` : '';
}

// The core platform's canonical Phone object (868jyyy8j):
// { country: 'us', dialCode: '1', phone: '2025551234' }. Country falls
// back to a dial-code lookup for DTOs from before the input carried it;
// with no resolvable country the phone is dropped (it is optional there).
export function toCanonicalPhone(value) {
  const dto = resolvePhoneDTO(value);

  if (!dto) {
    return null;
  }

  const country = dto.country || countryForDialCode(dto.code);

  if (!country) {
    return null;
  }

  return { country, dialCode: dto.code, phone: dto.number };
}

function countryForDialCode(code) {
  const target = String(code || '').replace(/\D/g, '');

  if (!target) {
    return null;
  }

  const match = defaultCountries.map(parseCountry).find((country) => country.dialCode === target);

  return match ? match.iso2 : null;
}
